// @flow
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  List,
  ListItemButton,
  ListItemText,
  SubmitButton,
  Tooltip,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
// eslint-disable-next-line max-len
import { Form } from '@fortress-technology-solutions/fortress-component-library/Organisms';
// eslint-disable-next-line max-len
import { Banner } from '@fortress-technology-solutions/fortress-component-library/Atoms';
// eslint-disable-next-line max-len
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
// eslint-disable-next-line max-len
import { AsyncBox } from '@fortress-technology-solutions/fortress-component-library/Molecules';
// eslint-disable-next-line max-len
import { useMediaQuery } from '@fortress-technology-solutions/fortress-component-library/hooks';
import {
  AddIcon,
  PencilIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import useIntl from '../../hooks/useIntl';
import generalMessages from '../App/messages';
import FeesAndDepositsList from './components/FeesAndDepositsList';
import SelectedProgramsTable from './components/SelectedProgramsTable';
import useConvertToApplicantModal from './hooks/useConvertToApplicantModal';
import { MODAL_MODES, SECTION_NAMES, getSections } from './constants';
import messages from './messages';

type Props = {
  open: boolean,
  onClose: Function,
  currentProspect: ?Object,
  floorPlans: ?Array<Object>,
  isSubmitting: ?boolean,
  mode?: string,
  applicationId?: ?string,
  onSubmit?: ?Function,
  submitDisabledTooltip?: React$Node,
};

// The theme caps dialog paper at 800px, too narrow for a nav beside the
// sections, so both maxWidth and width are overridden here.
const PAPER_SX = {
  width: { xs: 'calc(100% - 32px)', md: 1100 },
  maxWidth: { xs: 'calc(100% - 32px)', md: 1100 },
  maxHeight: 'calc(100vh - 100px)',
};

// The title sits inside the content pane, so the dialog title bar only carries
// the close icon and is collapsed to keep the nav flush with the top.
const TITLE_SX = { p: 0 };

const NAV_SX = {
  width: 240,
  flexShrink: 0,
  overflowY: 'auto',
  borderRight: 1,
  borderColor: 'divider',
  p: 2,
};

const NAV_ITEM_SX = {
  px: 2,
  py: 1,
  '&.Mui-selected:hover': { backgroundColor: 'primary.lighter' },
};

// The footer belongs to the content pane rather than to the dialog, so the nav
// runs the full height beside it. The pane holds the scroller and the footer,
// and only the scroller moves.
const CONTENT_SX = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const SECTIONS_SX = {
  flex: 1,
  overflowY: 'auto',
  p: 3,
  backgroundColor: 'background.alt',
};

const FOOTER_SX = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 1,
  p: 3,
  borderTop: 1,
  borderColor: 'divider',
};

// Each mode's own wording. Reinstate deliberately shares edit's: both open on a saved record and
// ask the agent to review it, and the copy has read that way since ENG-30176.
const COPY_BY_MODE = {
  [MODAL_MODES.CONVERT]: {
    title: messages.modalTitle,
    caption: messages.caption,
    missingFields: messages.missingFields,
    submitLabel: messages.modalTitle,
  },
  [MODAL_MODES.ADD]: {
    title: messages.addModalTitle,
    caption: messages.addCaption,
    missingFields: messages.addMissingFields,
    submitLabel: messages.addSubmitLabel,
  },
  [MODAL_MODES.EDIT]: {
    title: messages.editModalTitle,
    caption: messages.editCaption,
    missingFields: messages.editMissingFields,
    submitLabel: generalMessages.save,
  },
  [MODAL_MODES.REINSTATE]: {
    title: messages.editModalTitle,
    caption: messages.editCaption,
    missingFields: messages.editMissingFields,
    submitLabel: generalMessages.save,
  },
};

const ConvertToApplicantModal = ({
  open,
  onClose,
  currentProspect,
  floorPlans,
  isSubmitting,
  mode = MODAL_MODES.CONVERT,
  applicationId,
  onSubmit,
  submitDisabledTooltip,
}: Props) => {
  const intl = useIntl();
  const hideNav = useMediaQuery('down', 'md');
  const isConvert = mode === MODAL_MODES.CONVERT;
  const isAdd = mode === MODAL_MODES.ADD;
  const modeCopy = COPY_BY_MODE[mode] ?? COPY_BY_MODE[MODAL_MODES.EDIT];
  const sections = useMemo(() => getSections(mode), [mode]);
  const {
    reactHookFormProps,
    sectionFields,
    submit,
    isConverting,
    missingFieldLabels,
    standaloneErrorMessages,
    isConventionalOnly,
    firstSectionLabelKey,
    programRows,
    unavailableProgramTooltip,
    waitlistClosedBannerMessage,
    feeRows,
    isIncomeLevelError,
    isPrefillLoading,
  } = useConvertToApplicantModal({
    currentProspect,
    floorPlans,
    onClose,
    mode,
    applicationId,
    onSubmit,
  });

  // The nav follows clicks only. Scrolling deliberately leaves the selection
  // alone: a scroll spy moved it under users mid-form and they reported it.
  const [selectedSection, setSelectedSection] = useState(sections[0].name);
  const sectionRefs = useRef({});

  const setSectionRef = useCallback(
    (name: string) => (node: ?HTMLElement) => {
      sectionRefs.current[name] = node;
    },
    [],
  );

  const handleSectionChange = useCallback(
    (name: string) => () => {
      setSelectedSection(name);
      sectionRefs.current[name]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    },
    [],
  );

  const sectionLabels = useMemo(
    () => ({
      [SECTION_NAMES.INCOME]: intl.formatMessage(
        messages[firstSectionLabelKey],
      ),
      [SECTION_NAMES.PRIMARY_APPLICANT]: intl.formatMessage(
        messages.primaryApplicant,
      ),
      [SECTION_NAMES.UNITS_AND_PREFERENCES]: intl.formatMessage(
        messages.unitsAndPreferences,
      ),
      [SECTION_NAMES.FEES_AND_DEPOSITS]: intl.formatMessage(
        messages.feesAndDeposits,
      ),
    }),
    [intl, firstSectionLabelKey],
  );

  const copy = useMemo(
    () => ({
      icon:
        isConvert || isAdd ? (
          <AddIcon fontSize="small" color="info" />
        ) : (
          <PencilIcon fontSize="small" color="info" />
        ),
      title: intl.formatMessage(modeCopy.title),
      caption: intl.formatMessage(modeCopy.caption),
      submitLabel: intl.formatMessage(modeCopy.submitLabel),
      missingFieldsTemplate: modeCopy.missingFields,
    }),
    [intl, isConvert, isAdd, modeCopy],
  );

  // Banner takes one string and renders it through a single Typography, so a
  // second red bar or a newline would both be lost. The mode's sentence about
  // the fields it can name and the messages carried by the ones it cannot are
  // joined into that one paragraph instead. The terminating stop is added here
  // rather than written into the copy, so the template reads as a sentence on
  // its own and no translation has to carry punctuation for this join's sake.
  const submitErrorMessage = useMemo(
    () =>
      [
        missingFieldLabels.length
          ? `${intl.formatMessage(copy.missingFieldsTemplate, {
              fields: missingFieldLabels.join(', '),
            })}.`
          : null,
        ...standaloneErrorMessages,
      ]
        .filter(Boolean)
        .join(' '),
    [intl, copy, missingFieldLabels, standaloneErrorMessages],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      hideActions
      TitleProps={{ sx: TITLE_SX }}
      ModalProps={{ PaperProps: { sx: PAPER_SX } }}
      ContentProps={{ sx: { display: 'flex', p: 0, overflow: 'hidden' } }}
    >
      {hideNav ? null : (
        <Box component="nav" sx={NAV_SX}>
          <List disablePadding>
            {sections.map(({ name }) => {
              const selected = selectedSection === name;
              return (
                <ListItemButton
                  key={name}
                  selected={selected}
                  onClick={handleSectionChange(name)}
                  sx={NAV_ITEM_SX}
                >
                  <ListItemText
                    disableTypography
                    primary={
                      <Typography
                        variant="bodyMedium"
                        color={selected ? 'primary.main' : 'text.secondary'}
                      >
                        {sectionLabels[name]}
                      </Typography>
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      )}
      <Box sx={CONTENT_SX}>
        <Box sx={SECTIONS_SX}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
            {copy.icon}
            <Typography variant="titleMedium">{copy.title}</Typography>
          </Box>
          <Typography variant="bodyMedium" color="text.secondary">
            {copy.caption}
          </Typography>
          {waitlistClosedBannerMessage ? (
            <Banner
              status="info"
              sx={{ mt: 2, width: '100%', maxWidth: '100%' }}
              message={waitlistClosedBannerMessage}
            />
          ) : null}
          {submitErrorMessage ? (
            <Banner
              status="error"
              sx={{ mt: 2, width: '100%', maxWidth: '100%' }}
              message={submitErrorMessage}
            />
          ) : null}
          <AsyncBox loading={isPrefillLoading}>
            {sections.map(({ name }) => (
              <Box
                key={name}
                ref={setSectionRef(name)}
                data-section={name}
                sx={{
                  backgroundColor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 3,
                  mt: 2,
                }}
              >
                <Typography variant="titleMedium" component="h4" mb={2}>
                  {sectionLabels[name]}
                </Typography>
                <Form
                  ReactHookFormProps={reactHookFormProps}
                  fields={sectionFields[name]}
                />
                {name === SECTION_NAMES.INCOME && !isConventionalOnly ? (
                  <SelectedProgramsTable
                    programRows={programRows}
                    ReactHookFormProps={reactHookFormProps}
                    isIncomeLevelError={isIncomeLevelError}
                    unavailableTooltip={unavailableProgramTooltip}
                  />
                ) : null}
                {name === SECTION_NAMES.FEES_AND_DEPOSITS ? (
                  <FeesAndDepositsList feeRows={feeRows} />
                ) : null}
              </Box>
            ))}
          </AsyncBox>
        </Box>
        <Box sx={FOOTER_SX}>
          <Button variant="text" onClick={onClose}>
            {intl.formatMessage(generalMessages.cancel)}
          </Button>
          {/* The button stays live with fields missing so the refusal can say
              which ones; nothing saves until they are filled. Prefill is the
              one exception: edit/reinstate render their fields inside an
              AsyncBox, and shouldUnregister drops anything not yet mounted, so
              a submit landing before the record arrives would validate an empty
              form and PATCH the record blank. isPrefillLoading is false in
              convert, which has no prefill, so that flow is unchanged. */}
          {submitDisabledTooltip ? (
            <Tooltip title={submitDisabledTooltip} placement="top">
              <span>
                <SubmitButton variant="primary" disabled isSubmitting={false}>
                  {copy.submitLabel}
                </SubmitButton>
              </span>
            </Tooltip>
          ) : (
            <SubmitButton
              variant="primary"
              disabled={isPrefillLoading}
              isSubmitting={Boolean(isSubmitting) || isConverting}
              onClick={submit}
            >
              {copy.submitLabel}
            </SubmitButton>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ConvertToApplicantModal;
